/**
 * Side cart upsell (Search & Discovery recommendations)
 */

(() => {
  const STORAGE_KEY = 'we:sideCartUpsellBaseProductId';
  const DEBUG_KEY = 'we:debugSideCartUpsells';
  const UPSELL_SECTION_ID = 'side-cart-upsells';

  const debugEnabled = () => {
    try {
      if (window.Shopify && window.Shopify.designMode) return true;
      return localStorage.getItem(DEBUG_KEY) === '1';
    } catch (e) {
      return false;
    }
  };

  const debugLog = (...args) => {
    if (!debugEnabled()) return;
    // eslint-disable-next-line no-console
    console.debug('[SideCartUpsells]', ...args);
  };

  const safeJsonParse = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch (e) {
      return fallback;
    }
  };

  const decodeHtmlEntities = (str) => {
    if (!str || typeof str !== 'string') return str;
    return str
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  };

  const getSideCartElement = () => document.getElementById('sidebar-cart');
  const getUpsellContainer = (sideCartEl) => sideCartEl?.querySelector('[data-side-cart-upsells]');

  const getSideCartSettings = (sideCartEl) => {
    if (!sideCartEl) return {};
    const raw = sideCartEl.getAttribute('data-section-settings') || '{}';
    return safeJsonParse(decodeHtmlEntities(raw), {});
  };

  const parseIdList = (csv) =>
    (csv || '')
      .split(',')
      .map((x) => parseInt(String(x).trim(), 10))
      .filter((n) => Number.isFinite(n) && n > 0);

  const formatUpsellPriceHtml = (choice) => {
    const now = choice?.price_formatted ?? '';
    const was = choice?.compare_at_price_formatted ?? '';
    const hasSale = Number(choice?.compare_at_price || 0) > Number(choice?.price || 0);

    if (hasSale && was) {
      return `<span class="SideCartUpsellTile__PriceNow">${now}</span><span class="SideCartUpsellTile__PriceWas">${was}</span>`;
    }
    return `<span class="SideCartUpsellTile__PriceNow">${now}</span>`;
  };

  const initUpsellTile = (tileEl) => {
    const choices = safeJsonParse(tileEl.dataset.upsellChoices || '[]', []);
    if (!Array.isArray(choices) || choices.length === 0) return;

    const selectEl = tileEl.querySelector('[data-upsell-color-select]'); // legacy native select (if present)
    const dropdownEl = tileEl.querySelector('[data-upsell-dropdown]');
    const dropdownToggle = tileEl.querySelector('[data-upsell-color-toggle]');
    const dropdownValue = tileEl.querySelector('[data-upsell-color-value]');
    const dropdownMenu = tileEl.querySelector('[data-upsell-color-menu]');
    const priceEl = tileEl.querySelector('[data-upsell-price]');
    const imageEl = tileEl.querySelector('[data-upsell-image]');
    const addBtn = tileEl.querySelector('[data-upsell-add]');

    const findChoiceByLabel = (label) => choices.find((c) => String(c.label) === String(label));

    const applyChoice = (choice) => {
      if (!choice) return;
      tileEl.dataset.selectedVariantId = String(choice.id);
      tileEl.dataset.selectedItemStatus = choice.item_status ? String(choice.item_status) : '';
      if (priceEl) priceEl.innerHTML = formatUpsellPriceHtml(choice);
      if (addBtn) addBtn.disabled = choice.available === false;
      if (imageEl && choice.image_url) {
        imageEl.src = choice.image_url;
        // Avoid stale responsive candidates if any
        if (imageEl.hasAttribute('srcset')) imageEl.removeAttribute('srcset');
      }
    };

    if (dropdownEl && dropdownToggle && dropdownValue && dropdownMenu) {
      // Initialize from current value text
      applyChoice(findChoiceByLabel(dropdownValue.textContent) || choices[0]);

      dropdownToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const isOpen = dropdownEl.classList.toggle('is-open');
        dropdownToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });

      dropdownMenu.querySelectorAll('[data-upsell-color-option]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const label = btn.getAttribute('data-upsell-color-label') || btn.textContent;
          dropdownValue.textContent = label;
          applyChoice(findChoiceByLabel(label) || choices[0]);

          dropdownMenu.querySelectorAll('[data-upsell-color-option]').forEach((x) => x.setAttribute('aria-selected', 'false'));
          btn.setAttribute('aria-selected', 'true');

          dropdownEl.classList.remove('is-open');
          dropdownToggle.setAttribute('aria-expanded', 'false');
        });
      });
    } else if (selectEl) {
      applyChoice(findChoiceByLabel(selectEl.value) || choices[0]);
      selectEl.addEventListener('change', () => applyChoice(findChoiceByLabel(selectEl.value) || choices[0]));
    } else {
      applyChoice(choices[0]);
    }

    if (addBtn) {
      addBtn.addEventListener('click', async () => {
        const selectedVariantId = parseInt(tileEl.dataset.selectedVariantId || '', 10);
        if (!Number.isFinite(selectedVariantId)) return;

        const itemStatus = (tileEl.dataset.selectedItemStatus || '').trim();
        const vendor = tileEl.dataset.vendor || '';
        const properties = itemStatus ? { 
          'Item Status': itemStatus,
          _vendor: vendor,
          _purchased_by_upsell: 'cart_upsells'
        } : {
          _vendor: vendor,
          _purchased_by_upsell: 'cart_upsells'
        };

        addBtn.disabled = true;
        tileEl.classList.add('is-adding');

        try {
          const response = await fetch(`${window.routes.cartAddUrl}.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ id: selectedVariantId, quantity: 1, ...(properties ? { properties } : {}) }),
          });

          if (!response.ok) {
            const err = await response.json().catch(() => null);
            console.error('Side cart upsell add failed', err || response.status);
            return;
          }

          document.documentElement.dispatchEvent(
            new CustomEvent('product:added', {
              bubbles: true,
              detail: { variant: { id: selectedVariantId }, quantity: 1 },
            })
          );
        } catch (e) {
          console.error('Side cart upsell add error', e);
        } finally {
          tileEl.classList.remove('is-adding');
          addBtn.disabled = false;
        }
      });
    }
  };

  const initUpsells = (containerEl) => {
    containerEl.querySelectorAll('[data-side-cart-upsell-tile]').forEach(initUpsellTile);
  };

  const loadSideCartUpsells = async () => {
    const sideCartEl = getSideCartElement();
    if (!sideCartEl) return;

    const containerEl = getUpsellContainer(sideCartEl);
    if (!containerEl) return;

    const settings = getSideCartSettings(sideCartEl);
    if (settings.upsellEnabled === false) {
      containerEl.innerHTML = '';
      return;
    }

    const cartProductIds = parseIdList(containerEl.dataset.cartProductIds);
    if (cartProductIds.length === 0) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
      containerEl.innerHTML = '';
      return;
    }

    let baseProductId = parseInt((() => {
      try {
        return localStorage.getItem(STORAGE_KEY) || '';
      } catch (e) {
        return '';
      }
    })(), 10);

    if (!Number.isFinite(baseProductId) || !cartProductIds.includes(baseProductId)) {
      baseProductId = parseInt(containerEl.dataset.firstCartProductId || '', 10) || cartProductIds[0];
      if (Number.isFinite(baseProductId)) {
        try {
          localStorage.setItem(STORAGE_KEY, String(baseProductId));
        } catch (e) {}
      }
    }

    if (!Number.isFinite(baseProductId)) {
      debugLog('no valid baseProductId', { cartProductIds });
      containerEl.innerHTML = '';
      return;
    }

    const intent = settings.upsellIntent === 'complementary' ? 'complementary' : 'related';
    const baseQuery =
      `${window.routes.productRecommendationsUrl}` +
      `?section_id=${encodeURIComponent(UPSELL_SECTION_ID)}` +
      `&product_id=${encodeURIComponent(String(baseProductId))}` +
      `&limit=10` +
      `&timestamp=${Date.now()}`;
    const urlWithIntent = `${baseQuery}&intent=${encodeURIComponent(intent)}`;
    const urlNoIntent = baseQuery;

    try {
      const fetchAndExtract = async (url) => {
        debugLog('fetching', { url, baseProductId, intent });
        const response = await fetch(url, { credentials: 'same-origin', method: 'GET' });
        const html = await response.text();
        debugLog('response', { status: response.status, length: html.length });
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return { html, temp };
      };

      let { html, temp } = await fetchAndExtract(urlWithIntent);

      let contentEl = temp.querySelector('[data-side-cart-upsells-content]');
      if (!contentEl) {
        debugLog('no content with intent, retrying without intent');
        ({ html, temp } = await fetchAndExtract(urlNoIntent));
        contentEl = temp.querySelector('[data-side-cart-upsells-content]');
      }

      if (!contentEl) {
        if (html.includes('Liquid error') || html.includes('Liquid syntax error')) {
          console.error('Side cart upsell Liquid error in response:', html);
        }
        containerEl.innerHTML = '';
        return;
      }

      containerEl.innerHTML = contentEl.outerHTML;
      initUpsells(containerEl);
    } catch (e) {
      console.error('Side cart upsell load error', e);
      containerEl.innerHTML = '';
    }
  };

  document.addEventListener('cart:content-replaced', () => loadSideCartUpsells());

  document.addEventListener('click', (e) => {
    const trigger = e.target?.closest?.('[data-action="open-drawer"][data-drawer-id="sidebar-cart"]');
    if (!trigger) return;
    setTimeout(() => loadSideCartUpsells(), 0);
  });

  // Close any open upsell dropdowns when clicking outside
  document.addEventListener('click', (e) => {
    const inside = e.target?.closest?.('[data-upsell-dropdown]');
    if (inside) return;
    document.querySelectorAll('[data-upsell-dropdown].is-open').forEach((el) => {
      el.classList.remove('is-open');
      const toggle = el.querySelector('[data-upsell-color-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  });

  document.addEventListener('DOMContentLoaded', () => loadSideCartUpsells());
})();


